/**
 * The slideshow used on the Content Hub landing pages. 
 * 
 * It has a "next"/"previous" navigation and also a
 * text summary navigation above. It also has an "infinite loop".
 *
 * Notes:
 * - Because the design (on Desktop at least) shows 1 slide before + after the current slide, we clone 2 slides 
 * at each end to prevent "empty space" when crossing the boundary
 * - .active is applied by logical slide id (data-slideshow-slide), so clones + originals match and the snap 
 * doesn't cause an opacity flash
 */

const SWIPE_THRESHOLD_PX = 50
const VISIBLE_NEIGHBOURS_EACH_SIDE = 1
const CLONE_BUFFER = VISIBLE_NEIGHBOURS_EACH_SIDE + 1

const slideshow_states = []
let summary_click_listener_added = false
let resize_listener_added = false

function setup()
{
	const $slideshows = document.querySelectorAll('[data-slideshow]')
	if ($slideshows.length === 0) return

	if (!summary_click_listener_added)
	{
		summary_click_listener_added = true

		document.addEventListener('click', (e) =>
		{
			const $button = e.target.closest('[data-slideshow-summary-button]')
			if (!$button) return

			const $slideshow = $button.closest('[data-slideshow]')
			if (!$slideshow) return

			const slideshow_index = Number($slideshow.getAttribute('data-slideshow-index'))
			const slideshow_state = slideshow_states[slideshow_index]
			if (!slideshow_state) return

			const slide_id = Number($button.getAttribute('data-slideshow-slide'))
			if (Number.isNaN(slide_id)) return

			const clamped = Math.max(0, Math.min(slide_id, slideshow_state.slide_count - 1))
			slideshow_state.current_index = slideshow_state.slide_count > 1 ? clamped + slideshow_state.clone_buffer : 0
			update_slide_position(slideshow_state)
		})
	}

	if (!resize_listener_added)
	{
		resize_listener_added = true

		window.addEventListener('resize', () =>
		{
			slideshow_states.forEach((slideshow_state) =>
			{
				update_slide_position(slideshow_state)
			})
		})
	}

	let index = 0
	$slideshows.forEach($container =>
	{
		// Prevent double-initialising if setup() is ever called again
		if ($container.getAttribute('data-slideshow-initialised') === 'true')
		{
			index++
			return
		}
		$container.setAttribute('data-slideshow-initialised', 'true')

		$container.setAttribute('data-slideshow-index', index)

		const $slides_wrapper = $container.querySelector('[data-slideshow-slides]')
		const $real_slides = Array.from($slides_wrapper.querySelectorAll('[data-slideshow-slide]'))
		const slide_count = $real_slides.length
		const clone_buffer = Math.min(CLONE_BUFFER, slide_count)

		if (slide_count > 1)
		{
			// Prepend last N slides (KEEP ORDER): [..., C, D] becomes [Cclone, Dclone, ...]
			const $prepend_fragment = document.createDocumentFragment()
			$real_slides.slice(-clone_buffer).forEach(($slide) =>
			{
				const $clone = $slide.cloneNode(true)
				$clone.setAttribute('data-slideshow-clone', 'true')
				$prepend_fragment.appendChild($clone)
			})
			$slides_wrapper.insertBefore($prepend_fragment, $slides_wrapper.firstChild)

			// Append first N slides (order already correct): [A, B] -> [Aclone, Bclone]
			const $append_fragment = document.createDocumentFragment()
			$real_slides.slice(0, clone_buffer).forEach(($slide) =>
			{
				const $clone = $slide.cloneNode(true)
				$clone.setAttribute('data-slideshow-clone', 'true')
				$append_fragment.appendChild($clone)
			})
			$slides_wrapper.appendChild($append_fragment)
		}

		const slideshow_state = {
			$container: $container,
			$slides_wrapper: $slides_wrapper,
			$slides: $slides_wrapper.querySelectorAll('[data-slideshow-slide]'),
			slide_count: slide_count,
			clone_buffer: clone_buffer,
			current_index: slide_count > 1 ? clone_buffer : 0,
			is_dragging: false,
			start_x: 0,
		}

		slideshow_states.push(slideshow_state)

		$container.querySelector('[data-slideshow-nav-previous]').addEventListener('click', () => showPrev(slideshow_state))
		$container.querySelector('[data-slideshow-nav-next]').addEventListener('click', () => showNext(slideshow_state))

		if (slide_count > 1)
		{
			$slides_wrapper.addEventListener('transitionend', (e) =>
			{
				if (e.propertyName !== 'transform') return
				if (slideshow_state.is_dragging) return

				const left_clone_end = slideshow_state.clone_buffer - 1
				const right_clone_start = slideshow_state.clone_buffer + slideshow_state.slide_count

				if (slideshow_state.current_index <= left_clone_end)
				{
					slideshow_state.$slides_wrapper.style.transition = 'none'
					slideshow_state.current_index += slideshow_state.slide_count
					update_slide_position(slideshow_state)
					slideshow_state.$slides_wrapper.offsetHeight
					slideshow_state.$slides_wrapper.style.transition = ''
				}
				else if (slideshow_state.current_index >= right_clone_start)
				{
					slideshow_state.$slides_wrapper.style.transition = 'none'
					slideshow_state.current_index -= slideshow_state.slide_count
					update_slide_position(slideshow_state)
					slideshow_state.$slides_wrapper.offsetHeight
					slideshow_state.$slides_wrapper.style.transition = ''
				}
			})
		}

		$slides_wrapper.addEventListener('touchstart', (e) =>
		{
			if (slideshow_state.slide_count <= 1) return
			if (e.touches.length !== 1) return

			slideshow_state.start_x = e.touches[0].clientX
			slideshow_state.is_dragging = true
			$slides_wrapper.style.transition = 'none'
		})

		$slides_wrapper.addEventListener('touchmove', (e) =>
		{
			if (!slideshow_state.is_dragging) return
			if (e.touches.length !== 1) return

			const delta_x = e.touches[0].clientX - slideshow_state.start_x
			const gap = parseFloat(window.getComputedStyle(slideshow_state.$slides_wrapper).columnGap) || 0

			const offset = Array.from(slideshow_state.$slides)
				.slice(0, slideshow_state.current_index)
				.reduce((acc, slide) => acc + slide.getBoundingClientRect().width + gap, 0)

			$slides_wrapper.style.transform = `translateX(calc(-${offset}px + ${delta_x}px))`
		})

		$slides_wrapper.addEventListener('touchend', (e) =>
		{
			if (slideshow_state.slide_count <= 1) return
			if (!slideshow_state.is_dragging) return

			const end_x = e.changedTouches[0].clientX
			const delta = end_x - slideshow_state.start_x

			slideshow_state.is_dragging = false
			$slides_wrapper.style.transition = ''

			if (Math.abs(delta) > SWIPE_THRESHOLD_PX)
			{
				delta < 0 ? showNext(slideshow_state) : showPrev(slideshow_state)
				return
			}

			update_slide_position(slideshow_state)
		})

		$slides_wrapper.addEventListener('touchcancel', () =>
		{
			if (!slideshow_state.is_dragging) return

			slideshow_state.is_dragging = false
			$slides_wrapper.style.transition = ''
			update_slide_position(slideshow_state)
		})

		update_slide_position(slideshow_state)

		index++
	})
}

function update_slide_position(slideshow_state)
{
	const gap = parseFloat(window.getComputedStyle(slideshow_state.$slides_wrapper).columnGap) || 0

	let offset = 0
	for (let i = 0; i < Math.min(slideshow_state.current_index, slideshow_state.$slides.length); i++)
	{
		const slide = slideshow_state.$slides[i]
		offset += slide.getBoundingClientRect().width + gap
	}

	slideshow_state.$slides_wrapper.style.transform = `translateX(-${offset}px)`

	let logical_index = 0
	if (slideshow_state.slide_count > 1)
	{
		logical_index = (slideshow_state.current_index - slideshow_state.clone_buffer) % slideshow_state.slide_count
		if (logical_index < 0) logical_index += slideshow_state.slide_count
	}

	// Mark active by logical id so clones + originals match (prevents opacity flash on snap)
	slideshow_state.$slides.forEach(($slide) =>
	{
		const slide_id = Number($slide.getAttribute('data-slideshow-slide'))
		$slide.classList.toggle('active', slide_id === logical_index)
	})

	const $slide_info = slideshow_state.$container.querySelector('[data-slideshow-slide-info]')
	if ($slide_info)
	{
		const real_render_index = slideshow_state.slide_count > 1
			? slideshow_state.clone_buffer + logical_index
			: 0

		const $real_slide = slideshow_state.$slides[real_render_index]
		if ($real_slide)
		{
			$slide_info.querySelector('[data-slideshow-slide-summary]').textContent =
				$real_slide.getAttribute('data-slideshow-slide-summary') || ''

			$slide_info.querySelector('[data-slideshow-slide-caption]').textContent =
				$real_slide.getAttribute('data-slideshow-slide-caption') || ''
		}
	}

	const $prev_button = slideshow_state.$container.querySelector('[data-slideshow-nav-previous]')
	const $next_button = slideshow_state.$container.querySelector('[data-slideshow-nav-next]')
	if ($next_button && $prev_button)
	{
		const is_looping = slideshow_state.slide_count > 1
		$prev_button.classList.toggle('active', is_looping)
		$next_button.classList.toggle('active', is_looping)
	}

	const $slideshow_summary = slideshow_state.$container.querySelector('[data-slideshow-summary]')
	if ($slideshow_summary)
	{
		$slideshow_summary.querySelectorAll('[data-slideshow-summary-button]').forEach((button, index) =>
		{
			button.classList.toggle('active', index === logical_index)
		})
	}
}

function showNext(slideshow_state)
{
	if (slideshow_state.slide_count <= 1) return

	const max_index = slideshow_state.$slides.length - 1
	if (slideshow_state.current_index < max_index)
	{
		slideshow_state.current_index++
		update_slide_position(slideshow_state)
	}
}

function showPrev(slideshow_state)
{
	if (slideshow_state.slide_count <= 1) return

	if (slideshow_state.current_index > 0)
	{
		slideshow_state.current_index--
		update_slide_position(slideshow_state)
	}
}

export default function init()
{
	if (document.readyState === 'loading')
	{
		document.addEventListener('DOMContentLoaded', setup)
	}
	else
	{
		setup()
	}
}
